// Authentication Context for MUU Platform
import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import {
  AuthState,
  AuthContextType,
  AuthUser,
  LoginRequest,
  LoginResponse,
  ChangePasswordRequest,
  JWTTokens,
  TokenPayload,
} from '../types/auth';
import { tokenService } from '../services/auth/tokenService';
import { userService } from '../services/userService';
import { profileService } from '../modules/profile/services/profileService';
import { asaasService } from '../services/asaasService';
import { normalizePaidPlanType } from '../modules/onboarding/services/onboardingService';
import { extractErrorMessage } from '../utils/errorHandler';

// Initial auth state
const initialAuthState: AuthState = {
  isAuthenticated: false,
  isLoading: true,
  user: null,
  tokens: null,
  error: null,
  lastActivity: Date.now(),
  sessionStartTime: null,
  hasCompleteProfile: false,
  profileId: null,
  hasActivePlan: false,
  planType: undefined,
};

// Auth action types
type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: { user: AuthUser; tokens: JWTTokens; hasCompleteProfile?: boolean; profileId?: string; hasActivePlan?: boolean; planType?: string } }
  | { type: 'AUTH_FAILURE'; payload: string }
  | { type: 'AUTH_LOGOUT' }
  | { type: 'AUTH_REFRESH_SUCCESS'; payload: JWTTokens }
  | { type: 'UPDATE_USER'; payload: Partial<AuthUser> }
  | { type: 'UPDATE_ACTIVITY' }
  | { type: 'UPDATE_PROFILE_STATUS'; payload: { hasCompleteProfile: boolean; profileId: string | null } }
  | { type: 'UPDATE_PLAN_STATUS'; payload: { hasActivePlan: boolean; planType?: string } }
  | { type: 'CLEAR_ERROR' };

// Auth reducer
function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };

    case 'AUTH_SUCCESS':
      return {
        ...state,
        isAuthenticated: true,
        isLoading: false,
        user: action.payload.user,
        tokens: action.payload.tokens,
        error: null,
        lastActivity: Date.now(),
        sessionStartTime: Date.now(),
        hasCompleteProfile: action.payload.hasCompleteProfile || false,
        profileId: action.payload.profileId || null,
        hasActivePlan: action.payload.hasActivePlan || false,
        planType: action.payload.planType || undefined,
      };

    case 'AUTH_FAILURE':
      return {
        ...state,
        isAuthenticated: false,
        isLoading: false,
        user: null,
        tokens: null,
        error: action.payload,
      };

    case 'AUTH_LOGOUT':
      return {
        ...initialAuthState,
        isLoading: false,
        sessionStartTime: null,
        hasCompleteProfile: false,
        profileId: null,
      };

    case 'AUTH_REFRESH_SUCCESS':
      return {
        ...state,
        tokens: action.payload,
        lastActivity: Date.now(),
      };

    case 'UPDATE_USER':
      return {
        ...state,
        user: state.user ? { ...state.user, ...action.payload } : null,
      };

    case 'UPDATE_ACTIVITY':
      return {
        ...state,
        lastActivity: Date.now(),
      };

    case 'UPDATE_PROFILE_STATUS':
      return {
        ...state,
        hasCompleteProfile: action.payload.hasCompleteProfile,
        profileId: action.payload.profileId,
      };

    case 'UPDATE_PLAN_STATUS':
      return {
        ...state,
        hasActivePlan: action.payload.hasActivePlan,
        planType: action.payload.planType || state.planType,
      };

    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };

    default:
      return state;
  }
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Auth provider props
interface AuthProviderProps {
  children: React.ReactNode;
}

// Auth provider component
export function AuthProvider({ children }: AuthProviderProps) {
  const [authState, dispatch] = useReducer(authReducer, initialAuthState);

  // Debug logging
  const debugLog = useCallback((message: string, data?: any) => {
    try {
      const ts = new Date().toISOString();
      if (data !== undefined) {
        console.info(`[AuthContext ${ts}]`, message, data);
      } else {
        console.info(`[AuthContext ${ts}]`, message);
      }
    } catch {}
  }, []);

  // Check user profile completeness
  const checkUserProfile = useCallback(async (username: string) => {
    try {

      
      const profileResult = await profileService.getProfileByUsername(username);
      
      if (profileResult.success && profileResult.data) {
        const profile = profileResult.data;
        
        // Verificar se o perfil está completo
        const isComplete = !!(profile.contact_details?.phone && profile.business_info?.business_type);
        

        
        dispatch({
          type: 'UPDATE_PROFILE_STATUS',
          payload: {
            hasCompleteProfile: isComplete,
            profileId: profile._key || null
          }
        });
        
        return { hasProfile: true, isComplete, profileId: profile._key || null };
      } else {

        
        dispatch({
          type: 'UPDATE_PROFILE_STATUS',
          payload: {
            hasCompleteProfile: false,
            profileId: null
          }
        });
        
        return { hasProfile: false, isComplete: false, profileId: null };
      }
    } catch (error) {

      return { hasProfile: false, isComplete: false, profileId: null };
    }
  }, []);

  // Initialize authentication state
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Mark auth state as loading to prevent premature route guards
        dispatch({ type: 'AUTH_START' });
        // Inicialização silenciosa do estado de autenticação
        // Migrate any legacy localStorage values to cookies (one-shot)
        try { tokenService.migrateLegacyStorageToCookies(); } catch {}
        
        const tokens = tokenService.getTokens();
        if (!tokens) {
          dispatch({ type: 'AUTH_FAILURE', payload: 'No authentication tokens' });
          return;
        }

        // Check if access token is expired
        if (tokenService.isTokenExpired(tokens.accessToken)) {
          const refreshSuccess = await tokenService.refreshTokens();
          if (refreshSuccess) {
            const updatedTokens = tokenService.getTokens();
            if (updatedTokens) {
              tokens.accessToken = updatedTokens.accessToken;
              tokens.refreshToken = updatedTokens.refreshToken;
            }
          }
        }

        // Get user data from token
        const tokenPayload = tokenService.decodeToken(tokens.accessToken);
        if (!tokenPayload) {
          debugLog('Invalid token payload');
          dispatch({ type: 'AUTH_FAILURE', payload: 'Invalid token payload' });
          return;
        }

        // Create AuthUser from token payload directly (no need for additional API call)
        const authUser: AuthUser = {
          id: tokenPayload.sub || tokenPayload.user_id || '',
          email: tokenPayload.email || '',
          username: tokenPayload.username || tokenPayload.email || '',
          full_name: tokenPayload.name || tokenPayload.full_name || tokenPayload.email || '',
          role: tokenPayload.role || 'user',
          phone: tokenPayload.phone || '',
          language_preference: tokenPayload.language_preference || 'pt',
          timezone: tokenPayload.timezone || 'America/Sao_Paulo',
          is_active: tokenPayload.is_active !== false,
          last_login: new Date().toISOString(),
          farm_role_assignments: tokenPayload.farm_role_assignments || [],
        };

        // Fallback de identidade a partir de cookies quando o token não contém email/username (com migração do localStorage)
        try {
          const storedAuthUser = tokenService.getAuthUserCookie();
          const before = { email: authUser.email, username: authUser.username };
          if (storedAuthUser) {
            authUser.email = authUser.email || storedAuthUser.email || '';
            authUser.username = authUser.username || storedAuthUser.username || storedAuthUser.email || '';
            authUser.full_name = authUser.full_name || storedAuthUser.full_name || authUser.email || '';
          }
          // Fallback adicional: se sub parecer um email, usar como email
          if (!authUser.email && typeof tokenPayload.sub === 'string' && tokenPayload.sub.includes('@')) {
            authUser.email = tokenPayload.sub;
          }
          const after = { email: authUser.email, username: authUser.username };
        } catch (e) {
          debugLog('Identity fallback error', e);
        }

        // Autenticação concluída
        
        // Verificar perfil após autenticação bem-sucedida
        const profileStatus = await checkUserProfile(authUser.username);

        // Verificar assinatura ativa via Asaas (sem webhook)
        let hasActivePlan = false;
        let planType: string | undefined = undefined;
        if (authUser.email) {
          try {
            const timeoutPromise = new Promise<any>((_, reject) => {
              setTimeout(() => reject(new Error('Asaas timeout - blocking access')), 15000);
            });
            
            const access = await Promise.race([
              asaasService.checkAccessByEmail(authUser.email),
              timeoutPromise
            ]) as any;
            
            hasActivePlan = Boolean(access?.accessGranted);
            planType = hasActivePlan ? normalizePaidPlanType(access?.planType) || undefined : undefined;
          } catch (error) {
            console.error('[AuthContext] Asaas access check failed, blocking access:', error);
            hasActivePlan = false;
          }
        }

        dispatch({ 
          type: 'AUTH_SUCCESS', 
          payload: { 
            user: { ...authUser, planType, subscriptionStatus: hasActivePlan ? 'active' : 'inactive' }, 
            tokens,
            hasCompleteProfile: profileStatus.isComplete,
            profileId: profileStatus.profileId || undefined,
            hasActivePlan,
            planType
          } 
        });

        // Persist authUser to cookie and update chat username (no separate cookies)
        try {
          tokenService.setAuthUserCookie(authUser);
          const chatModule = await import('../modules/agent/services/ChatSessionService');
          chatModule.updateChatSessionUsername(authUser.username);
        } catch (err) {
          console.error('Failed to persist/update chat username on init:', err);
        }
      } catch (error) {
        debugLog('Authentication initialization failed', error);
        dispatch({ type: 'AUTH_FAILURE', payload: 'Authentication initialization failed' });
      }
    };

    initializeAuth();
  }, [debugLog]);

  // Auto-refresh tokens
  useEffect(() => {
    if (!authState.isAuthenticated || !authState.tokens) return;

    const checkAndRefreshToken = async () => {
      if (tokenService.needsRefresh(authState.tokens?.accessToken)) {
        const refreshSuccess = await tokenService.refreshTokens();
        if (refreshSuccess) {
          const updatedTokens = tokenService.getTokens();
          if (updatedTokens) {
            dispatch({ type: 'AUTH_REFRESH_SUCCESS', payload: updatedTokens });
          }
        }
      }
    };

    // Check every 5 minutes
    const interval = setInterval(checkAndRefreshToken, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [authState.isAuthenticated, authState.tokens, debugLog]);

  // Login function
  const login = useCallback(async (credentials: LoginRequest): Promise<LoginResponse> => {
    try {
      dispatch({ type: 'AUTH_START' });

      // Call the actual login API endpoint
      const loginResult = await userService.authenticateWithJWT(credentials);

      if (!loginResult || !loginResult.accessToken) {
        throw new Error('Invalid credentials');
      }

      // Create tokens from API response
      const tokens: JWTTokens = {
        accessToken: loginResult.accessToken,
        refreshToken: loginResult.refreshToken,
        expiresAt: Date.now() + (loginResult.expiresIn * 1000),
        tokenType: 'Bearer',
      };

      // Store tokens
      tokenService.setTokens(tokens);

      // Use the user from API response
      const authUser: AuthUser = loginResult.user;

      
      // Verificar perfil após login bem-sucedido
      const profileStatus = await checkUserProfile(authUser.username);

      // Checar plano ativo via Asaas
      let hasActivePlan = false;
      let planType: string | undefined = undefined;
      if (authUser.email) {
        try {
          const timeoutPromise = new Promise<any>((_, reject) => {
            setTimeout(() => reject(new Error('Asaas timeout - blocking access')), 15000);
          });
          
          const access = await Promise.race([
            asaasService.checkAccessByEmail(authUser.email),
            timeoutPromise
          ]) as any;
          
          hasActivePlan = Boolean(access?.accessGranted);
          planType = hasActivePlan ? normalizePaidPlanType(access?.planType) || undefined : undefined;
        } catch (error) {
          console.error('[AuthContext] Login Asaas check failed, blocking access:', error);
          hasActivePlan = false;
        }
      }
      
      dispatch({ 
        type: 'AUTH_SUCCESS', 
        payload: { 
          user: { ...authUser, planType, subscriptionStatus: hasActivePlan ? 'active' : 'inactive' }, 
          tokens,
          hasCompleteProfile: profileStatus.isComplete,
          profileId: profileStatus.profileId || undefined,
          hasActivePlan,
          planType
        } 
      });
      
      // Persist identity to cookies for ChatSessionService and future initialization fallbacks
      const loginPayload = tokenService.decodeToken(tokens.accessToken);
      const storedAuthUser = tokenService.getAuthUserCookie();
      const effectiveUsername = authUser.username || loginPayload?.username || storedAuthUser?.username || storedAuthUser?.email || '';
      const effectiveEmail = authUser.email || loginPayload?.email || storedAuthUser?.email || '';
      const effectiveFullName = authUser.full_name || loginPayload?.full_name || storedAuthUser?.full_name || effectiveEmail || effectiveUsername;
      const persistUser = { ...authUser, username: effectiveUsername, email: effectiveEmail, full_name: effectiveFullName };
      try { tokenService.setAuthUserCookie(persistUser); } catch {}

      // Update ChatSessionService username
      import('../modules/agent/services/ChatSessionService').then(module => {
        module.updateChatSessionUsername(effectiveUsername);
      }).catch(err => console.error('Failed to update chat session username:', err));

      return {
        success: true,
        user: authUser,
        tokens,
        message: 'Login successful',
      };
    } catch (error) {
      const authErrorMessage = extractErrorMessage(error);
      dispatch({ type: 'AUTH_FAILURE', payload: authErrorMessage });

      return {
        success: false,
        user: {} as AuthUser,
        tokens: {} as JWTTokens,
        message: authErrorMessage,
      };
    }
  }, [debugLog]);

  // Login com Google removido (não utilizado)

  // Logout function
  const logout = useCallback(async (): Promise<void> => {
    try {
      
      // Clear tokens
      tokenService.clearTokens();
      
      // TODO: Call logout API endpoint
      
      // Clear identity cookies
      tokenService.clearUsername();
      tokenService.clearIdentityEmail();
      tokenService.clearAuthUserCookie();
 
       
       // Update ChatSessionService username to null/empty on logout
       import('../modules/agent/services/ChatSessionService').then(module => {
         module.updateChatSessionUsername('');
       }).catch(err => console.error('Failed to update chat session username:', err));
      
      dispatch({ type: 'AUTH_LOGOUT' });
    } catch (error) {
      debugLog('Logout error', error);
      // Still clear local state even if API call fails
      dispatch({ type: 'AUTH_LOGOUT' });
    }
  }, [debugLog]);

  // Refresh tokens function
  const refreshTokens = useCallback(async (): Promise<boolean> => {
    try {
      debugLog('Refreshing tokens');
      
      const success = await tokenService.refreshTokens();
      if (success) {
        const updatedTokens = tokenService.getTokens();
        if (updatedTokens) {
          dispatch({ type: 'AUTH_REFRESH_SUCCESS', payload: updatedTokens });
          debugLog('Token refresh successful');
          return true;
        }
      }
      
      debugLog('Token refresh failed');
      return false;
    } catch (error) {
      debugLog('Token refresh error', error);
      return false;
    }
  }, [debugLog]);

  // Update profile function
  const updateProfile = useCallback(async (updates: Partial<AuthUser>): Promise<void> => {
    try {
      if (!authState.user) {
        throw new Error('No authenticated user');
      }

      debugLog('Updating profile', updates);
      
      // TODO: Call update user API
      
      dispatch({ type: 'UPDATE_USER', payload: updates });
      debugLog('Profile update successful');
    } catch (error) {
      debugLog('Profile update failed', error);
      throw error;
    }
  }, [authState.user, debugLog]);

  // Change password function
  const changePassword = useCallback(async (request: ChangePasswordRequest): Promise<void> => {
    try {
      if (!authState.user) {
        throw new Error('No authenticated user');
      }

      debugLog('Changing password');
      
      // TODO: Call change password API
      
      debugLog('Password change successful');
    } catch (error) {
      debugLog('Password change failed', error);
      throw error;
    }
  }, [authState.user, debugLog]);

  // Check auth status function
  const checkAuthStatus = useCallback(async (): Promise<boolean> => {
    try {
      const tokens = tokenService.getTokens();
      if (!tokens) return false;

      if (tokenService.isTokenExpired(tokens.accessToken)) {
        const refreshSuccess = await tokenService.refreshTokens();
        return refreshSuccess;
      }

      return true;
    } catch (error) {
      debugLog('Auth status check failed', error);
      return false;
    }
  }, [debugLog]);

  // Clear auth data function
  const clearAuthData = useCallback((): void => {
    tokenService.clearTokens();
    dispatch({ type: 'AUTH_LOGOUT' });
  }, []);

  // Check if token is expired
  const isTokenExpired = useCallback((token?: string): boolean => {
    return tokenService.isTokenExpired(token);
  }, []);

  // Get token payload
  const getTokenPayload = useCallback((token?: string): TokenPayload | null => {
    return tokenService.decodeToken(token || authState.tokens?.accessToken || '');
  }, [authState.tokens]);

  // Update activity
  const updateActivity = useCallback(() => {
    dispatch({ type: 'UPDATE_ACTIVITY' });
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  // Refresh plan status (exposed to UI)
  const refreshPlanStatus = useCallback(async (): Promise<{ hasActivePlan: boolean; planType?: string }> => {
    try {
      const email = authState.user?.email || '';
      if (!email) return { hasActivePlan: false };
      const access = await asaasService.checkAccessByEmail(email);
      const hasActive = Boolean((access as any)?.accessGranted);
      const pType = hasActive ? normalizePaidPlanType((access as any)?.planType) || undefined : undefined;
      dispatch({ type: 'UPDATE_PLAN_STATUS', payload: { hasActivePlan: hasActive, planType: pType } });
      return { hasActivePlan: hasActive, planType: pType };
    } catch (e) {
      return { hasActivePlan: false };
    }
  }, [authState.user]);

  useEffect(() => {
    return () => {};
  }, []);

  // Context value
  const contextValue: AuthContextType = {
    authState,
    login,
    logout,
    refreshTokens,
    updateProfile,
    changePassword,
    checkUserProfile,
    checkAuthStatus,
    clearAuthData,
    isTokenExpired,
    getTokenPayload,
    refreshPlanStatus,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

// Hook to use auth context
export function useAuthContext(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
